import ReportLead from '../shared/ReportLead';
import { Box, Card, CardContent, Skeleton, Typography, useTheme } from '@mui/material';
import { analyticsColors, getReportSurfaceSx } from '../shared/report-design';

const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const percent = (value) => {
    const parsed = number(value);
    return parsed <= 1 ? parsed * 100 : parsed;
};

const labelForSource = (row) => row?.sourceMedium || [row?.source, row?.medium].filter(Boolean).join(' / ') || '(not set)';

export const buildGa4OverviewModel = ({ kpis = {}, conversionSummary = {}, trafficSources = [], conversionEvents = [] }) => {
    const users = number(kpis.users);
    const sessions = number(kpis.sessions);
    const newUsers = number(kpis.newUsers);
    const engagementRate = percent(kpis.engagementRate);
    const conversions = number(conversionSummary.conversions ?? kpis.conversions);
    const conversionRate = percent(conversionSummary.conversionRate ?? (sessions ? conversions / sessions : 0));
    const hasMeasuredEngagedSessions = kpis.engagedSessions != null;
    const engagedSessions = hasMeasuredEngagedSessions
        ? number(kpis.engagedSessions)
        : Math.round(sessions * (engagementRate / 100));
    const topSource = [...(Array.isArray(trafficSources) ? trafficSources : [])]
        .sort((a, b) => number(b.sessions) - number(a.sessions))[0];
    const topEvent = [...(Array.isArray(conversionEvents) ? conversionEvents : [])]
        .sort((a, b) => number(b.conversions) - number(a.conversions))[0];

    let standout = '';
    if (topSource && number(topSource.sessions) > 0) {
        standout = `${labelForSource(topSource)} led acquisition with ${number(topSource.sessions).toLocaleString()} sessions${number(topSource.conversions) ? ` and ${number(topSource.conversions).toLocaleString()} key events` : ''}.`;
    } else if (topEvent && number(topEvent.conversions) > 0) {
        standout = `${topEvent.name || topEvent.event || 'The leading key event'} accounted for ${number(topEvent.conversions).toLocaleString()} key events.`;
    }

    return {
        users,
        sessions,
        newUsers,
        engagementRate,
        conversions,
        conversionRate,
        engagedSessions,
        hasMeasuredEngagedSessions,
        newUserShare: users ? (newUsers / users) * 100 : 0,
        sessionsPerUser: users ? sessions / users : 0,
        topSource,
        topEvent,
        standout,
    };
};

export default function GA4Pulse({ dateLabel, siteLabel, kpis, conversionSummary, trafficSources, conversionEvents, loading, formatNumber, formatDuration }) {
    const theme = useTheme();
    const surface = getReportSurfaceSx(theme);
    const model = buildGa4OverviewModel({ kpis, conversionSummary, trafficSources, conversionEvents });
    const formatRate = (value) => `${number(value).toFixed(1)}%`;
    const stages = [
        { label: 'Acquisition', accent: analyticsColors.acquisition, description: 'Who arrived and how often', metrics: [
            { label: 'Users', value: formatNumber(model.users), detail: 'Active website audience' },
            { label: 'Sessions', value: formatNumber(model.sessions), detail: 'Visits recorded' },
        ] },
        { label: 'Engagement', accent: analyticsColors.engagement, description: 'How much useful attention the site earned', metrics: [
            { label: 'Engagement rate', value: formatRate(model.engagementRate), detail: 'Share of engaged sessions' },
            { label: 'Avg. engagement', value: formatDuration(kpis?.avgEngagementTime), detail: 'Active attention per user' },
        ] },
        { label: 'Outcome', accent: analyticsColors.outcome, description: 'Which meaningful actions followed', metrics: [
            { label: 'Key events', value: formatNumber(model.conversions), detail: 'Meaningful actions recorded' },
            { label: 'Session action rate', value: formatRate(model.conversionRate), detail: 'Key events relative to sessions' },
        ] },
    ];
    const supportMetrics = [
        { label: 'New users', value: formatNumber(model.newUsers) },
        { label: 'New-user share', value: formatRate(model.newUserShare) },
        { label: 'Engaged sessions', value: `${model.hasMeasuredEngagedSessions ? '' : '~'}${formatNumber(model.engagedSessions)}` },
        { label: 'Sessions per user', value: model.sessionsPerUser.toFixed(1) },
    ];
    const highlights = [
        { label: 'Top acquisition source', title: model.topSource ? labelForSource(model.topSource) : 'No source data', value: formatNumber(model.topSource?.sessions || 0), detail: 'Sessions from the leading source / medium.', accent: analyticsColors.acquisition },
        { label: 'Strongest action', title: model.topEvent?.name || model.topEvent?.event || 'No key-event data', value: formatNumber(model.topEvent?.conversions || 0), detail: 'Key events recorded for the leading action.', accent: analyticsColors.outcome },
        { label: 'Engagement signal', title: 'Engaged sessions', value: `${model.hasMeasuredEngagedSessions ? '' : '~'}${formatNumber(model.engagedSessions)}`, detail: `${formatRate(model.engagementRate)} engagement rate.`, accent: analyticsColors.engagement },
        { label: 'What stands out', title: model.topSource ? labelForSource(model.topSource) : 'Analytics readout', detail: model.standout || `${formatNumber(model.sessions)} sessions produced ${formatNumber(model.conversions)} key events.`, accent: analyticsColors.outcome },
    ];
    const valueSx = { fontSize: { xs: '1.6rem', md: '1.8rem' }, fontWeight: 750, lineHeight: 1.1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' };
    return <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <Card component="section" elevation={0} sx={surface}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                    <ReportLead eyebrow="Google Analytics 4" title="From audience to action" description="Understand who arrived, whether they engaged, and which meaningful actions followed." badge={siteLabel} />
                </Box>
                <Box role="group" aria-label="Website analytics performance indicators" sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
                    {stages.map((stage) => <Box key={stage.label} sx={{ minWidth: 0 }}>
                        <Typography component="h2" variant="subtitle2" sx={{ mb: 1, color: stage.accent, fontWeight: 700 }}>{stage.label}</Typography>
                        <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            {stage.metrics.map((metric) => <Box key={metric.label} sx={{ ...surface, p: 1.5, borderTop: `3px solid ${stage.accent}` }}>
                                <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                                {loading ? <Skeleton height={40} /> : <Typography sx={{ ...valueSx, mt: 1 }}>{metric.value}</Typography>}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{metric.detail}</Typography>
                            </Box>)}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{stage.description}</Typography>
                    </Box>)}
                </Box>
                <Box sx={{ mt: 2, display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                    {supportMetrics.map((metric) => <Box key={metric.label} sx={{ bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                        {loading ? <Skeleton /> : <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{metric.value}</Typography>}
                    </Box>)}
                </Box>
                {model.standout && !loading ? <Typography variant="body2" sx={{ mt: 2, borderLeft: `3px solid ${analyticsColors.acquisition}`, pl: 1.5 }}>{model.standout}</Typography> : null}
            </CardContent>
        </Card>
        <Box component="section" aria-label="Analytics highlights" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' } }}>
            {highlights.map((item) => <Card key={item.label} elevation={0} sx={surface}><CardContent>
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                {loading ? <Skeleton height={30} /> : <Typography component="h2" sx={{ fontWeight: 700, mt: 0.75, overflowWrap: 'anywhere' }}>{item.title}</Typography>}
                {item.value && (loading ? <Skeleton height={40} /> : <Typography sx={{ ...valueSx, color: item.accent, mt: 1 }}>{item.value}</Typography>)}
                {loading ? <Skeleton /> : <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{item.detail}</Typography>}
            </CardContent></Card>)}
        </Box>
    </Box>;
}
